import { useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Badge, Button, Card, Table } from '@dreamtree-org/twreact-ui';
import { Plus, X } from 'lucide-react';
import { EmptyState, Money, PageHeader } from '@/components';
import { useQuery } from '@/lib/useQuery';
import { formatDate } from '@/lib/format';
import { COMPARE_LIMIT, compareRows, lowestIndex, parseCompareIds } from '../lib/compare';
import { useStorefront } from '../StorefrontContext';
import { hasOptions } from '../lib/variants';
import UnitImage from '../components/UnitImage';
import CatalogGate from '../components/CatalogGate';

/**
 * WEB-04 compare up to 4 exact units side by side. The URL (`?ids=1,2`) is
 * shareable; missing values are shown explicitly ("Not specified"), never
 * blank, and there are no invented benchmark scores (C-03).
 */
export default function Compare() {
  const sf = useStorefront();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const fromUrl = params.get('ids');
  const ids = useMemo(() => (fromUrl ? parseCompareIds(fromUrl) : sf.compareIds), [fromUrl, sf.compareIds]);

  // keep the tray selection in step with a shared link
  useEffect(() => {
    if (fromUrl && ids.join(',') !== sf.compareIds.join(',')) sf.setCompare(ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromUrl]);

  const units = ids.map((id) => sf.byId(id)).filter(Boolean);
  const gone = sf.catalog.data ? ids.length - units.length : 0;

  const summary = useQuery('sfInspectionSummary', { units: units.map((u) => ({ unit_id: u.unit_id, grade: u.grade, brand: u.brand })) }, { select: (r) => r.data, enabled: units.length > 0 });
  const pin = useQuery('sfPinCheck', { pin: sf.pin }, { select: (r) => r.data, enabled: !!sf.pin });
  const seller = useQuery('sfSeller', { slug: sf.seller.slug, name: sf.seller.name }, { select: (r) => r.data });

  const extras = useMemo(() => {
    const out = {};
    for (const u of units) {
      const d = pin.data;
      out[u.id] = {
        inspection: summary.data && summary.data[u.unit_id],
        delivery: d && (d.serviceable ? { shipping_paise: d.shipping_paise, label: `Free to ${d.pin}, by ${formatDate(d.eta_date)}` } : { shipping_paise: null, label: `Not deliverable to ${d.pin}` }),
        terms: seller.data && { return_days: seller.data.policies.returns.return_days },
      };
    }
    return out;
  }, [units, summary.data, pin.data, seller.data]);

  const rows = compareRows(units, extras, units.length ? sf.shop.typeOf(units[0]) : null);

  const remove = (id) => {
    const next = ids.filter((x) => x !== id);
    sf.setCompare(next);
    setParams(next.length ? { ids: next.join(',') } : {});
  };

  const columns = [
    { key: 'label', label: 'Feature', render: (r) => <span className="font-medium">{r.label}</span> },
    ...units.map((u, i) => ({
      key: `u${u.id}`,
      label: `${u.brand} ${u.model_name}`,
      wrap: true,
      render: (r) => <Cell row={r} i={i} />,
    })),
  ];

  return (
    <div className="space-y-4">
      <PageHeader title="Compare" subtitle={`Up to ${COMPARE_LIMIT} exact units side by side. Values we don't have are marked “Not specified”.`} />
      <CatalogGate>
        {units.length === 0 ? (
          <EmptyState title="Nothing to compare yet" action={<Button onClick={() => navigate('/shop')}>Browse the shop</Button>}>
            Tick “Compare” on up to {COMPARE_LIMIT} items of the same kind, then come back here.
          </EmptyState>
        ) : (
          <div className="space-y-4">
            {gone > 0 ? <Alert variant="warning">{gone} item{gone > 1 ? "s were" : " was"} sold or unlisted and removed from this comparison.</Alert> : null}
            {units.length === 1 ? <Alert variant="info">Add at least one more to compare.</Alert> : null}

            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {units.map((u) => (
                <Card key={u.id} className="relative flex flex-col gap-2 p-3">
                  <span className="absolute right-1 top-1 z-10">
                    <Button size="xs" variant="ghost" aria-label={`Remove ${u.title} from compare`} onClick={() => remove(u.id)}><X className="h-4 w-4" /></Button>
                  </span>
                  <Link to={`/p/${u.id}`}><UnitImage size="sm" className="aspect-[4/3]" label={u.title} icon={(sf.shop.typeOf(u) || {}).icon} /></Link>
                  <Link to={`/p/${u.id}`} className="text-sm font-semibold leading-snug hover:underline">{u.brand} {u.series} {u.model_name}</Link>
                  <Money paise={u.price_paise} className="font-bold" />
                  <div className="mt-auto">
                    <Button size="sm" fullWidth variant={sf.inCart(u.id) && !hasOptions(u) ? 'outline' : 'primary'} onClick={() => (hasOptions(u) ? navigate(`/p/${u.id}`) : sf.inCart(u.id) ? navigate('/cart') : sf.addToCart(u))}>
                      {hasOptions(u) ? 'Choose options' : sf.inCart(u.id) ? 'In cart' : 'Add to cart'}
                    </Button>
                  </div>
                </Card>
              ))}
              {units.length < COMPARE_LIMIT ? (
                <Link to="/shop" className="block">
                  <Card className="flex h-full min-h-40 flex-col items-center justify-center gap-2 border-dashed p-3 text-sm opacity-80 hover:opacity-100">
                    <Plus className="h-6 w-6 text-primary-600" aria-hidden="true" />
                    Add another
                  </Card>
                </Link>
              ) : null}
            </div>

            {!sf.pin ? <p className="text-sm opacity-70">Check a PIN code on any product page to see delivery and all-in prices here.</p> : null}

            <Table
              data={rows}
              columns={columns}
              sortable={false}
              showSerial={false}
              withAction={false}
              showToolbar={false}
              showColumnPicker={false}
              showLimitSelector={false}
              showReloadButton={false}
              wrapCells
            />
          </div>
        )}
      </CatalogGate>
    </div>
  );
}

function Cell({ row, i }) {
  const value = row.values[i];
  if (value === null || value === undefined) return <Badge size="xs" variant="outline">Not specified</Badge>;
  if (!row.money) return <span>{value}</span>;
  const best = lowestIndex(row.values) === i && row.key !== 'mrp';
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <Money paise={value} className={row.key === 'mrp' ? 'opacity-70' : 'font-semibold'} />
      {best ? <Badge size="xs" variant="success">Lowest</Badge> : null}
    </span>
  );
}
